/**
 * Utility for fetching Wistia video thumbnails and converting them to base64
 * for seamless hero video loading with zero network delay
 */

interface WistiaOEmbedResponse {
  thumbnail_url?: string;
  width?: number;
  height?: number;
}

interface WistiaThumbnailResult {
  base64Image: string;
  thumbnailUrl: string;
  width?: number;
  height?: number;
}

/**
 * Modifies a Wistia thumbnail URL to request the first frame of the video
 * @param thumbnailUrl - Original thumbnail URL from oEmbed API
 * @param time - Time in seconds for the still frame (default: 0 for first frame)
 * @param size - Optional size parameter (e.g., "960x540")
 * @returns Modified URL with video_still_time parameter
 */
export function modifyWistiaThumbnailUrl(
  thumbnailUrl: string,
  time: number = 0,
  size?: string
): string {
  try {
    const url = new URL(thumbnailUrl);

    // Add video_still_time parameter to get specific frame
    url.searchParams.set("video_still_time", time.toString());

    // Optionally override the image size
    if (size) {
      url.searchParams.set("image_crop_resized", size);
    }

    const modifiedUrl = url.toString();
    return modifiedUrl;
  } catch (error) {
    console.error("Error modifying Wistia thumbnail URL:", error);
    return thumbnailUrl; // Return original URL if parsing fails
  }
}

/**
 * Fetches Wistia video metadata from oEmbed API (public, no authentication required)
 * @param videoId - Wistia video ID
 * @returns oEmbed response with thumbnail_url
 */
export async function fetchWistiaOEmbed(videoId: string): Promise<WistiaOEmbedResponse | null> {
  if (!videoId || !videoId.trim()) {
    console.error("fetchWistiaOEmbed: videoId is required");
    return null;
  }

  try {
    const mediaUrl = `https://home.wistia.com/medias/${encodeURIComponent(videoId)}`;
    const oembedUrl = `https://fast.wistia.com/oembed?url=${encodeURIComponent(mediaUrl)}`;

    const response = await fetch(oembedUrl, {
      // Cache for 24 hours to reduce API calls
      next: { revalidate: 86400 },
      headers: { "Accept": "application/json" }
    });

    if (!response.ok) {
      console.error(`Wistia oEmbed fetch failed: ${response.status} ${response.statusText}`);
      return null;
    }

    const data: WistiaOEmbedResponse = await response.json();
    return data;
  } catch (error) {
    console.error("Error fetching Wistia oEmbed data:", error);
    return null;
  }
}

/**
 * Converts an image URL to a base64 data URI
 * @param imageUrl - URL of the image to convert
 * @returns Base64 data URI string
 */
export async function imageUrlToBase64(imageUrl: string): Promise<string | null> {
  try {
    const response = await fetch(imageUrl, {
      // Cache images for 24 hours
      next: { revalidate: 86400 }
    });

    if (!response.ok) {
      console.error(`Failed to fetch image: ${response.status} ${response.statusText}`);
      return null;
    }

    const arrayBuffer = await response.arrayBuffer();
    const buffer = Buffer.from(arrayBuffer);
    const base64 = buffer.toString("base64");

    // Determine content type from response headers
    const contentType = response.headers.get("content-type") || "image/jpeg";

    return `data:${contentType};base64,${base64}`;
  } catch (error) {
    console.error("Error converting image to base64:", error);
    return null;
  }
}

/**
 * Main function: Fetches Wistia first-frame thumbnail and converts to base64
 * This is the primary function for server-side components
 *
 * @param videoId - Wistia video ID
 * @param fallbackImageUrl - Optional fallback image URL if Wistia fetch fails
 * @param size - Optional custom size (default: "960x540" for hero)
 * @returns Base64 image string and metadata, or null if all fetches fail
 */
export async function getWistiaFirstFrameBase64(
  videoId: string,
  fallbackImageUrl?: string,
  size: string = "960x540",
  timeInSeconds: number = 0
): Promise<WistiaThumbnailResult | null> {
  // Note: Wistia Data API v1 requires authentication (WISTIA_API_TOKEN).
  // If configured in the future, it can be added here for potentially better thumbnail quality.
  // Currently using oEmbed API which is public and works reliably.

  // Step 1: Fetch from Wistia oEmbed API (public, no authentication required)
  const oembedData = await fetchWistiaOEmbed(videoId);

  if (oembedData?.thumbnail_url) {
    // Step 2: Modify URL to request first frame at specific time
    const firstFrameUrl = modifyWistiaThumbnailUrl(oembedData.thumbnail_url, timeInSeconds, size);

    // Step 3: Convert to base64
    const base64Image = await imageUrlToBase64(firstFrameUrl);

    if (base64Image) {
      return {
        base64Image,
        thumbnailUrl: firstFrameUrl,
        width: oembedData.width,
        height: oembedData.height
      };
    }
  }

  // Fallback: Try to use provided fallback image
  if (fallbackImageUrl) {
    console.log("Wistia thumbnail fetch failed, attempting fallback image");
    const fallbackBase64 = await imageUrlToBase64(fallbackImageUrl);

    if (fallbackBase64) {
      return {
        base64Image: fallbackBase64,
        thumbnailUrl: fallbackImageUrl
      };
    }
  }

  console.error("All thumbnail fetch attempts failed");
  return null;
}
